import React from 'react';

export type SandboxErrorHandler = (error: Error, context: { source: 'infrastructure' | 'runtime'; message?: string }) => void;

interface SandboxErrorBoundaryProps {
  onError?: SandboxErrorHandler;
  children: React.ReactNode;
}

interface SandboxErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary for sandbox infrastructure failures — catches errors
 * from SandpackProvider itself (bundler unreachable, initialization crash, etc.).
 *
 * User JSX runtime errors inside the iframe don't bubble here — those are
 * caught by SandboxErrorReporter via Sandpack's error state.
 */
export class SandboxErrorBoundary extends React.Component<SandboxErrorBoundaryProps, SandboxErrorBoundaryState> {
  state: SandboxErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): SandboxErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error): void {
    this.props.onError?.(error, { source: 'infrastructure' });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: 24,
          gap: 12,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#64748b',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#334155' }}>
            Widget failed to load
          </div>
          <div style={{ fontSize: 12, maxWidth: 300 }}>
            {this.state.error?.message ?? 'The sandbox could not be initialized. This may be a network issue.'}
          </div>
          <button
            onClick={this.handleRetry}
            style={{
              marginTop: 4,
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              background: '#fff',
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
